/**
 * The "g" navigation chords (g then s = Students, ...). "g h" (home) is
 * separate: it always goes to homePathForRole.
 */

import type { PermissionValue, UserRoleValue } from "@/constants";
import { roleHasPermission } from "@/utils/permissions";

/**
 * A "g" chord. Office roles (admin, principal, staff) go to `path` when they
 * hold `permission` — the destination route's own ProtectedRoute gate. Portal
 * roles (teacher, parent, platform admin) have their own pages, so they get a
 * chord only where `portalPaths` names one: an office page would greet them
 * with Access Denied, or with buttons the API refuses.
 */
export interface NavChord {
  id: string;
  label: string;
  key: string;
  path: string;
  permission: PermissionValue;
  portalPaths?: Partial<Record<UserRoleValue, string>>;
}

const PORTAL_ROLES: readonly UserRoleValue[] = ["teacher", "parent", "super_user"];

export const NAV_CHORDS: readonly NavChord[] = [
  { id: "go-students", label: "Go to Students", key: "s", path: "/students", permission: "manage:students", portalPaths: { teacher: "/teacher/students" } },
  { id: "go-families", label: "Go to Families", key: "f", path: "/families", permission: "manage:families" },
  { id: "go-classes", label: "Go to Classes", key: "c", path: "/classes", permission: "manage:classes", portalPaths: { teacher: "/teacher/classes" } },
  { id: "go-invoices", label: "Go to Invoices", key: "i", path: "/invoices", permission: "manage:fees" },
  { id: "go-payments", label: "Go to Payments", key: "p", path: "/payments", permission: "manage:fees" },
  { id: "go-enrollments", label: "Go to Enrollments", key: "e", path: "/enrollments", permission: "manage:enrollments" },
  { id: "go-reports", label: "Go to Reports", key: "r", path: "/reports", permission: "view:reports" },
];

/** Where this chord takes this role, or null when it has no chord for them. */
export const resolveChordPath = (
  chord: NavChord,
  role: UserRoleValue | null | undefined,
): string | null => {
  if (!role) return null;
  if (PORTAL_ROLES.includes(role)) return chord.portalPaths?.[role] ?? null;
  return roleHasPermission(role, chord.permission) ? chord.path : null;
};
